type Sorter<T> = <U = unknown>(
  selector: (item: T) => U,
  customComparator?: (a: U, b: U) => number,
  direction?: 'asc' | 'desc'
) => SortFn<T>
type DescendingSorter<T> = <U = unknown>(
  selector: (item: T) => U,
  customComparator?: (a: U, b: U) => number
) => SortFn<T>

interface SortFn<T> {
  (a: T, b: T): number
  thenBy: Sorter<T>
  thenByDesc: DescendingSorter<T>
}

interface SortFn<T> {
  (a: T, b: T): number
  thenBy: Sorter<T>
  thenByDesc: DescendingSorter<T>
}

const defaultComparator = <T>(a: T, b: T) => (a < b ? -1 : 1)

export function comparator<T, R>(
  selector: (item: T) => R,
  customComparator: (a: R, b: R) => number = defaultComparator,
  direction: 'asc' | 'desc' = 'asc'
) {
  return (a: T, b: T) => {
    const aVal = selector(a)
    const bVal = selector(b)
    return aVal === bVal ? 0 : customComparator(aVal, bVal) * (direction === 'asc' ? 1 : -1)
  }
}
const numberComparator = (a: number, b: number) => (a === b ? 0 : a < b ? -1 : 1)
const stringComparator = (a: string, b: string) => a.localeCompare(b, 'en-US', { sensitivity: 'base' })
const booleanComparator = (a: boolean, b: boolean) => numberComparator(+a, +b)
export const cmp = {
  number: numberComparator,
  string: stringComparator,
  boolean: booleanComparator,
}

export function orderBy<T, R = unknown>(
  selector: (item: T) => R,
  customComparator: (a: R, b: R) => number = defaultComparator,
  direction: 'asc' | 'desc' = 'asc'
): SortFn<T> {
  const result = (a: T, b: T) => comparator(selector, customComparator, direction)(a, b)
  result.thenBy = <U = unknown>(
    s2: (item: T) => U,
    cmp2: (a: U, b: U) => number = defaultComparator,
    direction2: 'asc' | 'desc' = 'asc'
  ) =>
    orderBy<T, T>(
      (x) => x,
      (a, b) => result(a, b) || comparator(s2, cmp2, direction2)(a, b)
    )
  result.thenByDesc = <U = unknown>(s2: (item: T) => U, cmp2: (a: U, b: U) => number = defaultComparator) =>
    result.thenBy(s2, cmp2, 'desc')
  return result
}

export function orderByDesc<T, R = unknown>(
  selector: (item: T) => R,
  customComparator: (a: R, b: R) => number = defaultComparator
): SortFn<T> {
  return orderBy(selector, customComparator, 'desc')
}
